/**
 * Sawyer 15 workweek-hours-ot (E2) — near-overtime alert. Names anyone whose
 * projected Sunday-to-Saturday week crosses the owner's threshold, so Ted or
 * Sawyer can trim a shift before it becomes overtime. Computed on read from
 * GET /timesheets/workweek; nothing is sent (no bell exists yet, #880 open;
 * Sawyer did not want clock-in/out notifications).
 *
 * An alert, not a widget: with no alerts (or no data) it renders NOTHING, no
 * empty box and no skeleton. On the Dashboard a failed read also renders
 * nothing; the Timesheets panel shows the real error state.
 */
import { Link } from 'react-router-dom';
import { rolesHavePermission } from '../../lib/rbac-matrix.js';
import { overtimeAlertLine, type WorkweekHoursView } from '../../lib/workweek-api.js';
import { useWorkweekHours } from './useWorkweekHours.js';
import './workweek-hours.css';

const COMPACT_MAX_LINES = 3;

export interface OvertimeWatchBannerProps {
  data: WorkweekHoursView | null;
  /** Dashboard: at most 3 lines, then "and N more". */
  compact?: boolean;
  /** Dashboard: link to /timesheets#this-week instead of the in-page anchor. */
  linkToTimesheets?: boolean;
}

export function OvertimeWatchBanner({ data, compact = false, linkToTimesheets = false }: OvertimeWatchBannerProps): JSX.Element | null {
  const alerts = data && Array.isArray(data.alerts) ? data.alerts : [];
  if (alerts.length === 0) return null;
  const shown = compact ? alerts.slice(0, COMPACT_MAX_LINES) : alerts;
  const more = alerts.length - shown.length;
  return (
    <div
      className="ferro-alert ferro-alert--warning ferro-ts-otwatch"
      role="status"
      data-testid="overtime-watch"
    >
      <div className="ferro-ts-otwatch__body">
        <p className="ferro-ts-otwatch__title">Overtime watch, this week</p>
        <ul className="ferro-ts-otwatch__list">
          {shown.map((a) => (
            <li key={a.staff_user_id} data-testid={`overtime-watch-${a.level}`}>
              {overtimeAlertLine(a)}
            </li>
          ))}
          {more > 0 ? <li data-testid="overtime-watch-more">and {more} more</li> : null}
        </ul>
      </div>
      {linkToTimesheets ? (
        <Link className="ferro-btn ferro-btn--sm ferro-btn--outline ferro-ts-otwatch__link" to="/timesheets#this-week" data-testid="overtime-watch-link">
          See this week
        </Link>
      ) : (
        <a className="ferro-btn ferro-btn--sm ferro-btn--outline ferro-ts-otwatch__link" href="#this-week" data-testid="overtime-watch-link">
          See this week
        </a>
      )}
    </div>
  );
}

/** Owner and manager: the people who can change a shift. Mirrors the server
 *  route gate (payroll:read:all) AND the approve write; the accountant reads
 *  hours on Timesheets but gets no Dashboard banner. */
export function canWatchOvertime(roles: ReadonlyArray<string>): boolean {
  return rolesHavePermission(roles, 'payroll:read:all') && rolesHavePermission(roles, 'payroll.clock:write:all');
}

export interface DashboardOvertimeWatchProps {
  apiUrl: string;
  roles: ReadonlyArray<string>;
  fetchesEnabled: boolean;
  refreshKey: number;
}

/** The Dashboard mount. The role gate decides whether the FETCHING child
 *  mounts at all, so a receptionist, groomer or accountant fires no request
 *  (the canSeeRevenue pattern). */
export function DashboardOvertimeWatch({ apiUrl, roles, fetchesEnabled, refreshKey }: DashboardOvertimeWatchProps): JSX.Element | null {
  if (!canWatchOvertime(roles)) return null;
  return <DashboardOvertimeWatchFetch apiUrl={apiUrl} fetchesEnabled={fetchesEnabled} refreshKey={refreshKey} />;
}

function DashboardOvertimeWatchFetch({
  apiUrl,
  fetchesEnabled,
  refreshKey,
}: Omit<DashboardOvertimeWatchProps, 'roles'>): JSX.Element | null {
  const { state } = useWorkweekHours(apiUrl, null, { enabled: fetchesEnabled, refreshKey });
  if (state.kind !== 'ready') return null; // loading or failed: nothing (an alert, not a widget)
  return <OvertimeWatchBanner data={state.data} compact linkToTimesheets />;
}
